import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, type AnimationProps, motion } from "framer-motion";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { ZIndex } from "~/common/constants";
import { cn } from "~/common/redesign-v1/cn";
import { X } from "~/common/redesign-v1/icons";
import { Button } from "~/common/redesign-v1/ui";
import { PersonName } from "~/components/common/PersonName";

import type { TExpert } from "./meetings-types";

/**
 * This modal has no dedicated Figma frame. It uses the redesign typography, controls and colour tokens.
 * Video detection and booking-slot behavior remain shared with the legacy flow.
 */

const animationProps: AnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/** vkvideo.ru/clip-{oid}_{id} или vk.com/... → embed URL для iframe */
function getVkEmbedUrl(videoUrl: string): string | null {
  const s = (videoUrl || "").trim();
  const clipMatch = s.match(/vkvideo\.ru\/clip-(\d+)_(\d+)/);
  if (clipMatch) {
    const [, oid, id] = clipMatch;
    return `https://vkvideo.ru/video_ext.php?oid=-${oid}&id=${id}&hd=3`;
  }
  if (s.includes("video_ext.php")) return s;
  return null;
}

/** Есть ли доступный слот для записи (не раньше чем через 1 час) */
function hasAvailableSlot(expert: TExpert): boolean {
  if (!expert.nearest_slot?.times?.length) return false;
  const now = new Date();
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);
  return expert.nearest_slot.times.some(({ time }) => new Date(time) > minBookingTime);
}

export function ExpertAboutModal({ expert, isOpen, onOpenChange }: { expert: TExpert | null; isOpen: boolean; onOpenChange(open: boolean): void }) {
  const intl = useIntl();
  if (!expert) return null;

  const videoUrl = expert.video_url ?? null;
  const embedUrl = videoUrl ? getVkEmbedUrl(videoUrl) : null;
  const description = expert.description ?? null;
  const specializations = expert.specializations ?? [];
  const canBook = hasAvailableSlot(expert);

  return (
    <AnimatePresence>
      {isOpen ? (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
          <Dialog.Portal>
            <motion.div {...animationProps} className="tw-fixed tw-inset-0" style={{ zIndex: ZIndex.CENTERED_DIALOG }}>
              {/* `<html data-redesign-v1>` is set by `RedesignRoot` for as long as this page tree is mounted
                  (redesign-root.tsx), so the token vars already resolve here despite the Radix portal
                  rendered outside the React tree — no extra `data-redesign-v1` marker needed (same as the
                  shared `AlertDialog`). */}
              <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-backdrop" />

              <Dialog.Content
                className={cn(
                  "tw-fixed tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2",
                  "tw-flex tw-max-h-[90vh] tw-w-[calc(100%-2rem)] tw-max-w-2xl tw-flex-col tw-overflow-y-auto",
                  "tw-rounded-2xl tw-border tw-border-solid tw-border-border tw-bg-background tw-shadow-lg",
                  "sm:tw-h-[min(85vh,640px)] sm:tw-max-w-3xl sm:tw-flex-row sm:tw-items-stretch sm:tw-overflow-hidden",
                )}
                style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
              >
                <Dialog.Description className="tw-sr-only">
                  <FormattedMessage id="meetings.about_expert.dialog_description" /> <PersonName person={expert} useMiddleName />
                </Dialog.Description>

                <Dialog.Close
                  onClick={() => onOpenChange(false)}
                  aria-label={intl.formatMessage({ id: "alert.close", defaultMessage: "Close" })}
                  className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-foreground tw-transition-colors hover:tw-bg-accent"
                >
                  <X className="tw-size-4" aria-hidden />
                </Dialog.Close>

                {/* The portrait video keeps a 9:16 box. On desktop its width follows the modal height to avoid
                    letterboxing or an empty gap below the player. */}
                <div className="tw-mx-auto tw-aspect-[9/16] tw-w-[min(60vw,240px)] tw-shrink-0 tw-overflow-hidden tw-rounded-xl tw-bg-canvas-accent sm:tw-mx-0 sm:tw-h-full sm:tw-w-auto sm:tw-max-w-[420px]">
                  <div className="tw-relative tw-size-full">
                    {embedUrl ? (
                      <iframe
                        title="Видеовизитка эксперта"
                        src={embedUrl}
                        className="tw-absolute tw-inset-0 tw-size-full tw-border-0"
                        allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
                      />
                    ) : (
                      <a
                        href={videoUrl ?? "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-font-sans tw-text-sm tw-font-medium tw-text-muted-foreground hover:tw-underline"
                      >
                        <FormattedMessage id="meetings.about_expert.watch_video" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Text column — same typography tokens as everywhere else in the redesign: title =
                    paragraph-large/medium (matches other dialog titles), body = paragraph-small/regular,
                    section header = paragraph-small/semibold (the `FieldLabel` convention). */}
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-4 tw-p-6">
                  <Dialog.Title className="tw-m-0 tw-pr-8 tw-font-rd-accent tw-text-xl tw-font-normal tw-leading-[27px] tw-text-foreground">
                    <PersonName person={expert} useMiddleName />
                  </Dialog.Title>

                  {description ? <p className="tw-m-0 tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-foreground">{description}</p> : null}

                  {specializations.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-[5px]">
                      <span className="tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-text-foreground">
                        <FormattedMessage id="meetings.about_expert.specializations" />
                      </span>
                      <ul className="tw-m-0 tw-list-disc tw-space-y-1 tw-pl-5 tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-foreground">
                        {specializations.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="tw-mt-auto tw-flex tw-flex-wrap tw-gap-3 tw-pt-2">
                    <Button variant="outline" size="lg" className="tw-flex-1" onClick={() => onOpenChange(false)} data-testid="rd-expert-about-close">
                      <FormattedMessage id="meetings.about_expert.got_it" />
                    </Button>
                    {canBook ? (
                      <Button size="lg" className="tw-flex-1" asChild data-testid="rd-expert-about-book">
                        <Link to={`/pages/meetings/book/${expert.account_id}`}>
                          <FormattedMessage id="meetings.book" />
                        </Link>
                      </Button>
                    ) : (
                      <Button size="lg" className="tw-flex-1" disabled data-testid="rd-expert-about-book">
                        <FormattedMessage id="meetings.book" />
                      </Button>
                    )}
                  </div>
                </div>
              </Dialog.Content>
            </motion.div>
          </Dialog.Portal>
        </Dialog.Root>
      ) : null}
    </AnimatePresence>
  );
}
